import {
  AvailabilityStatus,
  type SponsoredProduct,
} from '@fdc/__generated__/graphql';
import {
  type AddToCartUnitType,
  getCartLineQuantityForUnit,
  getInitialAddValue,
  getInitialUnitType,
  resolveCartUnitOfMeasure,
  variantUomFromCartUnit,
} from '@fdc/components/ui/product-tile/add-to-cart-helpers';
import {
  formatPrice,
  getCupAndMemberPricing,
} from '@fdc/components/ui/product-tile/price-panel-helper';
import {
  DEFAULT_QUANTITY,
  DEFAULT_WEIGHT,
} from '@fdc/components/ui/product-tile/product-tile-constants';
import { ProductTileProps } from '@fdc/components/ui/product-tile/product-tile.types';
import { NAVIGATION_CONSTANTS } from '@fdc/constants/navigation.constants';
import type {
  HandleAddToCartClick,
  HandleStepperChange,
} from '@fdc/hooks/nz/mutations/useAddToCartOrStepper';
import {
  TagZones,
  availabilityStatusLabels,
  baseProductTagData,
  useTagZones,
} from '@fdc/hooks/useTagZone';
import { ProductSummaryUnion } from '@fdc/types';
import {
  AddToCartListAnalyticsContext,
  pushCartQuantityChangeAnalytics,
} from '@fdc/utils/add-to-cart-analytics';
import {
  getUomFromVariantKey,
  getVariantByUom,
  resolveActiveVariant,
  resolveAlternativeVariantKey,
  toTileVariants,
} from '@fdc/utils/variant-summary-utils';

export function isSponsoredProduct(
  product: Partial<ProductSummaryUnion>
): product is SponsoredProduct {
  return product?.__typename === 'SponsoredProduct';
}

export function getProductAdId(
  product: Partial<ProductSummaryUnion>
): string | undefined {
  if (!isSponsoredProduct(product)) {
    return undefined;
  }
  return product?.adId;
}

const purchaseUnitDefaultQty = (
  purchaseUnit?: ProductSummaryUnion['variants'][number]['purchaseUnit'],
  selectedType: AddToCartUnitType = 'quantity'
) => {
  if (!purchaseUnit) {
    return undefined;
  }
  return getInitialAddValue(selectedType, purchaseUnit);
};

function resolveSelectedUnitType(
  product: ProductSummaryUnion,
  options: {
    cartSelectedUom?: string | null;
    cartQuantity?: number;
  } = {}
): AddToCartUnitType {
  const variants = product.variants ?? [];
  const hasMultipleUom = variants.length > 1;

  return getInitialUnitType({
    purchasingUnits: variants[0]?.purchaseUnit,
    productContainsBothUom: hasMultipleUom,
    cartSelectedUom: options.cartSelectedUom,
    cartQuantity: options.cartQuantity,
  });
}

/** Resolves the variant used for list/tile analytics (each vs weight). */
export function resolveActiveVariantForAnalytics(
  product: Partial<ProductSummaryUnion>,
  options?: {
    cartSelectedUom?: string | null;
    cartQuantity?: number;
  }
): ProductSummaryUnion['variants'][number] | undefined {
  const variants = product.variants ?? [];
  if (!variants.length) {
    return undefined;
  }

  const selectedType = resolveSelectedUnitType(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: refactor to remove type assertion
    product as ProductSummaryUnion,
    options
  );
  return (
    getVariantByUom(
      variants,
      variantUomFromCartUnit(undefined, selectedType)
    ) ?? variants[0]
  );
}

export function mapProductSummaryToTileProduct(
  productSummary: ProductSummaryUnion,
  tagZones: TagZones
): ProductTileProps['product'] {
  const variants = productSummary.variants || [];

  const availabilityStatus = variants[0]?.availabilityStatus;
  const availabilityLabel = availabilityStatusLabels?.[availabilityStatus];

  if (availabilityLabel) {
    tagZones.productTag = [{ ...baseProductTagData, label: availabilityLabel }];
  }

  const weightVariant = getVariantByUom(variants, 'kg');
  const eachVariant = getVariantByUom(variants, 'each');
  const tileVariants = toTileVariants(variants);

  const weightVariantPrice = weightVariant?.variantPrice;
  const eachVariantPrice = eachVariant?.variantPrice;

  const basePrice =
    weightVariantPrice?.sellingPrice ?? eachVariantPrice?.sellingPrice;

  const {
    memberPrice,
    weightCupPrice,
    eachCupPrice,
    nonMemberPrice,
    nonMemberCupPrice,
    labelType,
  } = getCupAndMemberPricing({
    memberTag: tagZones?.memberPrice,
    productWeightVariant: weightVariantPrice,
    productEachVariant: eachVariantPrice,
    basePrice,
  });

  const price = memberPrice || basePrice;

  const priceWas = weightVariantPrice?.wasPrice ?? eachVariantPrice?.wasPrice;
  const priceUnit =
    weightVariantPrice?.sellingUnit?.toLowerCase() === 'ea'
      ? weightVariant?.purchaseUnit?.unit || ''
      : weightVariantPrice?.sellingUnit || '';

  return {
    productId: productSummary.sku,
    description: productSummary.productName || '',
    slug: productSummary.slug ?? '',
    imageUrl: productSummary.imageUrl,
    isAlcoholAgeRestricted: !!productSummary.isAlcohol,
    // TODO: map isAvailable & isPurchasable to product availability once data available
    isAvailable: true,
    isPurchasable: availabilityStatus === AvailabilityStatus.InStock,
    labelType: labelType,
    price: formatPrice(price),
    priceUnit: priceUnit?.toLowerCase() ?? '',
    priceWas: formatPrice(priceWas),
    restrictedItem: !!productSummary.isTobacco,
    tagZones,
    weightCupPrice,
    eachCupPrice,
    nonMemberPrice: memberPrice ? formatPrice(nonMemberPrice) : undefined,
    nonMemberCupPrice: memberPrice ? nonMemberCupPrice : undefined,
    variants: tileVariants,
    defaultQuantity:
      purchaseUnitDefaultQty(eachVariant?.purchaseUnit, 'quantity') ??
      DEFAULT_QUANTITY,
    defaultWeight:
      purchaseUnitDefaultQty(weightVariant?.purchaseUnit, 'weight') ??
      DEFAULT_WEIGHT,
  };
}

export function mapProductSummary(
  product: ProductSummaryUnion,
  cartLineItem:
    | { quantity?: number | null; productVariantSku?: string | null }
    | undefined,
  handleAddToCartClick: HandleAddToCartClick,
  handleStepperChange: HandleStepperChange,
  listContext?: AddToCartListAnalyticsContext & { itemListIndex: number },
  selectedTypeOverride?: AddToCartUnitType
): ProductTileProps {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const tagZones = useTagZones(product.tags);
  const variants = product.variants || [];
  const cartUom = getUomFromVariantKey(
    variants,
    cartLineItem?.productVariantSku
  );
  const selectedType =
    selectedTypeOverride ??
    resolveSelectedUnitType(product, {
      cartSelectedUom: cartUom,
      cartQuantity: cartLineItem?.quantity ?? undefined,
    });
  const activeVariant =
    getVariantByUom(
      variants,
      variantUomFromCartUnit(undefined, selectedType)
    ) ?? variants[0];
  const unitOfMeasure = resolveCartUnitOfMeasure(
    selectedType,
    activeVariant?.purchaseUnit
  );
  const quantity = getCartLineQuantityForUnit(
    { quantity: cartLineItem?.quantity, uom: cartUom },
    unitOfMeasure
  );
  const adId = getProductAdId(product);

  const onAddToCartClick = (sku: string, newValue: number) => {
    const activeVariant = resolveActiveVariant(
      variants,
      unitOfMeasure,
      selectedType
    );
    if (!activeVariant?.variantKey) {
      return;
    }

    const sanitizedQuantity = handleAddToCartClick({
      sku,
      newValue,
      unitOfMeasure,
      purchasingUnits: activeVariant.purchaseUnit,
      selectedVariantKey: activeVariant.variantKey,
      alternativeVariantKey: resolveAlternativeVariantKey(
        variants,
        unitOfMeasure,
        selectedType
      ),
      adId,
    });

    if (listContext) {
      const analyticsOptions = {
        ...listContext.analyticsOptions,
        cartSelectedUom: cartUom,
        cartQuantity: quantity,
      };

      pushCartQuantityChangeAnalytics({
        product,
        fromQty: quantity,
        toQty: sanitizedQuantity,
        itemListId: listContext.itemListId,
        itemListName: listContext.itemListName,
        itemListIndex: listContext.itemListIndex ?? 0,
        options: analyticsOptions,
      });
    }
  };

  const onCartQuantityChange = (_sku: string, newValue: number) => {
    const activeVariant = resolveActiveVariant(
      variants,
      unitOfMeasure,
      selectedType
    );
    if (!activeVariant?.variantKey) {
      return;
    }

    const sanitizedQuantity = handleStepperChange({
      newValue,
      unitOfMeasure,
      purchasingUnits: activeVariant.purchaseUnit,
      variantKey: activeVariant.variantKey,
      adId,
    });

    if (listContext) {
      const analyticsOptions = {
        ...listContext.analyticsOptions,
        cartSelectedUom: cartUom,
        cartQuantity: quantity,
      };

      pushCartQuantityChangeAnalytics({
        product,
        fromQty: quantity,
        toQty: sanitizedQuantity,
        itemListId: listContext.itemListId,
        itemListName: listContext.itemListName,
        itemListIndex: listContext.itemListIndex ?? 0,
        options: analyticsOptions,
      });
    }
  };

  return {
    product: mapProductSummaryToTileProduct(product, tagZones),
    productDetailsPageBaseUrl: NAVIGATION_CONSTANTS.PRODUCT_DETAILS_PAGE,
    onAddToCartClick,
    onCartQuantityChange,
    quantity,
    weight: selectedType === 'weight' ? quantity : undefined,
  };
}
